/*
 * A promotion's details and terms (board SF 05b; GUARDRAILS G9), opened by
 * tapping a ticket or its ⓘ on the Promotions page: the title and the
 * offer's name, where it stands for this client on its badge's colour, the
 * rows, the terms with ticks, then the main button and Close. Every line is
 * written from the offer's settings (utils/promotionDetails); the server
 * still decides what a booking gets. A bottom sheet on a phone, a dialog in
 * the middle on a computer (DECISIONS D10).
 */
import React, { useEffect, useRef } from 'react';
import PromotionBadge, { badgeFor } from './PromotionBadge';
import { CheckIcon, CloseIcon } from '../BookingNext/ServicesStep/icons';
import { detailRows, detailStatus, detailSub, detailTerms } from '../../utils/promotionDetails';
import type { Promotion } from '../../types/promotion.types';
import styles from './Promotions.module.css';

interface Props {
  promotion: Promotion;
  /** The main button: "Book now" for theirs, "Claim" for one to claim; none on Review and book. */
  primary?: { label: string; onClick: () => void; busy?: boolean };
  error?: string;
  onClose: () => void;
}

/** The status line's ink: a dark tone of the badge's own colour, as the board draws orange (#8A4B00). */
const INK: Record<string, string> = {
  '#12A150': '#0A6B34',
  '#F08C00': '#8A4B00',
  '#0066FF': '#0047B3',
  '#E5384F': '#A3172B',
  '#0E9AA0': '#0A6B70',
  '#7B4EE0': '#5530A8',
};

const PromotionDetails: React.FC<Props> = ({ promotion: p, primary, error, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const badge = badgeFor(p.icon);
  const status = detailStatus(p);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.dScrim} onClick={onClose} role="presentation">
      <div
        className={styles.dSheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.dGrab} />
        <div className={styles.dHead}>
          <span className={styles.dHeadText}>
            <h2 id="promo-details-title" className={styles.dTitle}>
              {p.title}
            </h2>
            <span className={styles.dSub}>{detailSub(p)}</span>
          </span>
          <button ref={closeRef} type="button" className={styles.dX} aria-label="Close" onClick={onClose}>
            <CloseIcon size={22} />
          </button>
        </div>
        <div className={styles.dScroll}>
          <div className={styles.dStatus} style={{ background: badge.tint }} data-status>
            <PromotionBadge icon={p.icon} size={44} />
            <span className={styles.dStatusText}>
              <b className={styles.dStatusTitle}>{status.title}</b>
              <span className={status.why ? styles.why : undefined} style={status.why ? undefined : { color: INK[badge.color] ?? '#4E5969' }}>
                {status.line}
              </span>
            </span>
          </div>
          <dl className={styles.dRows}>
            {detailRows(p).map((r) => (
              <div key={r.label} className={styles.dRow}>
                <dt>{r.label}</dt>
                <dd>{r.value}</dd>
              </div>
            ))}
          </dl>
          <h3 className={styles.dLabel}>Terms</h3>
          <ul className={styles.dTerms}>
            {detailTerms(p).map((t) => (
              <li key={t}>
                <span className={styles.dTick}>
                  <CheckIcon size={14} />
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.dFoot}>
          {error && <span className={styles.error}>{error}</span>}
          {primary && (
            <button type="button" className={styles.dPrimary} disabled={primary.busy} onClick={primary.onClick}>
              {primary.busy ? 'Claiming…' : primary.label}
            </button>
          )}
          <button type="button" className={styles.dClose} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionDetails;
